<template>
  <div>

    <v-row align="center" dense class="py-1" no-gutters>
      <v-col
          cols="12"
          sm="3"
          md="3"
          xl="3"
          lg="3"
          class="text-sm-left text-xs-center pb-1 pb-sm-0"
          style="font-size: 0.9rem; font-weight: bold"
      >
        Select Evidence Source:
      </v-col>

      <v-col cols="12" sm="4" md="4" xl="4" lg="4">
        <v-select
            v-model="selectedFilter"
            :items="filterOptions"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details
        />
      </v-col>

      <v-spacer></v-spacer>

      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" class="ml-2">
            <v-icon>mdi-menu</v-icon>
          </v-btn>
        </template>

        <v-list dense>
          <v-list-item @click="exportPNG">
            <v-list-item-title>Download PNG image</v-list-item-title>
          </v-list-item>
          <v-list-item @click="exportSVG">
            <v-list-item-title>Download SVG vector image</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-row>

    <v-row class="mt-n4">
      <v-col>
        <div id="lollipop-plot" class="lollipop-container">
          <!-- ✅ Loading overlay，不影响布局 -->
          <div
              v-if="isLoading"
              class="lollipop-loading"
          >
            <v-progress-circular
                indeterminate
                size="40"
                width="4"
                color="primary"
            />
          </div>
        </div>
        <div id="lp-tooltip" class="kb-tooltip"></div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import $ from "jquery";
import * as d3 from "d3";
import {lollipop} from '@/config/lollipop_ptm.js'
import {apiprefix} from "@/main";

export default {
  name: "LollipopChartNew",
  props: ["protein_id", "tdata"],

  data() {
    return {
      selectedFilter: "text+figure",
      filterOptions: [
        { text: "All", value: "all" },
        { text: "Text mining", value: "text-mining" },
        { text: "Figure mining", value: "figure-mining" },
        { text: "Text + Figure mining", value: "text+figure" },
        { text: "PTMAtlas datasets", value: "experiment" },
        { text: "CPTAC datasets", value: "cohort" }
      ],

      lastProteinId: null,
      cachedSeq: null,
      cachedPfams: null,

      resizeObserver: null,
      rafId: null,

      isPlotting: false,
      isLoading: false,
      lastRenderKey: null
    };
  },

  computed: {
    filteredData() {
      const data = Array.isArray(this.tdata) ? this.tdata : [];
      switch (this.selectedFilter) {
        case "all":
          return data;
        case "text-mining":
          return data.filter(d => d.Mutation_Class === "text-mining");
        case "figure-mining":
          return data.filter(d => d.Mutation_Class === "figure-mining");
        case "text+figure":
          return data.filter(d =>
              ["text-mining", "figure-mining"].includes(d.Mutation_Class)
          );
        case "experiment":
          return data.filter(d => d.Mutation_Class === "experiment");
        case "cohort":
          return data.filter(d => d.Mutation_Class === "cohort");
        default:
          return data;
      }
    }
  },

  methods: {
    schedulePlot() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.plotLollipop(this.protein_id);
      });
    },

    clearLollipop() {
      d3.select("#lollipop-plot").selectAll("svg").remove();
      d3.select("#lollipop-plot").selectAll(".lollipop-empty").remove();
    },

    showNoData() {
      this.clearLollipop();
      d3.select("#lollipop-plot")
          .append("div")
          .attr("class", "lollipop-empty")
          .text("No data.");
    },

    async plotLollipop(protein_id) {
      if (this.isPlotting) return;

      this.isPlotting = true;
      this.isLoading = true;

      try {
        const container = document.getElementById("lollipop-plot");
        if (!container || container.clientWidth === 0) return;

        if (!this.filteredData.length) {
          this.showNoData();
          return;
        }
        const renderKey =
            `${protein_id}_${this.selectedFilter}_${this.filteredData.length}`;
        if (this.lastRenderKey === renderKey) return;
        this.lastRenderKey = renderKey;

        this.clearLollipop();

        const summarizedMap = {};
        for (const item of this.filteredData) {
          const {
            Mutation_Type,
            Protein_Change,
            Mutation_Class,
            AA_Position
          } = item;

          if (AA_Position == null || !Protein_Change) continue;

          const key = `${AA_Position}_${Protein_Change}`;
          const mt = Mutation_Type || "unknown";
          const mc = Mutation_Class || "unknown";

          if (!summarizedMap[key]) {
            summarizedMap[key] = {
              Protein_Change,
              AA_Position,
              Mutation_Type: {},
              total_count: 0
            };
          }

          const entry = summarizedMap[key];
          entry.total_count++;
          entry.Mutation_Type[mt] ??= {};
          entry.Mutation_Type[mt][mc] ??= 0;
          entry.Mutation_Type[mt][mc]++;
        }

        const summarizedList = Object.values(summarizedMap);
        if (!summarizedList.length) {
          this.showNoData();
          return;
        }

        let seq, pfams;
        if (
            this.lastProteinId === protein_id &&
            this.cachedSeq &&
            this.cachedPfams
        ) {
          seq = this.cachedSeq;
          pfams = this.cachedPfams;
        } else {
          const currentProtein = protein_id;

          const seqRes = await $.getJSON(
              `https://rest.ensembl.org/sequence/id/${protein_id}?content-type=application/json`
          );
          const pfamRes = await $.getJSON(
              `https://rest.ensembl.org/overlap/translation/${protein_id}?type=pfam&content-type=application/json`
          );

          if (this.protein_id !== currentProtein) return;

          seq = seqRes.seq;
          pfams = pfamRes;

          this.cachedSeq = seq;
          this.cachedPfams = pfams;
          this.lastProteinId = protein_id;
        }

        lollipop(
            seq,
            pfams,
            {
              margin: { top: 30, right: 20, bottom: 40, left: 50 },
              svg_width: container.clientWidth,
              svg_height: container.clientHeight || 320,
              seqplot_height: 30,
              domain_height: 16,
              seq_height: 6,
              domain_colors: this.lightColorScale()
            },
            summarizedList
        );
      } catch (err) {
        console.error("[LollipopChart] render failed:", err);
      } finally {
        this.isPlotting = false;
        this.isLoading = false; // ⭐ 关闭 loading
      }
    },

    lightColorScale() {
      return d3.scaleOrdinal([
        "#77AADD", "#EE8866", "#EEDD88", "#FFAABB",
        "#99DDFF", "#44BB99", "#BBCC33", "#AAAA00", "#DDDDDD"
      ]);
    },

    exportSVG() {
      const svgEl = document.querySelector("#lollipop-plot svg");
      if (!svgEl) return;

      const serializer = new XMLSerializer();
      const source = serializer.serializeToString(svgEl);
      const blob = new Blob([source], {
        type: "image/svg+xml;charset=utf-8"
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${this.protein_id}_lollipop.svg`;
      a.click();
      URL.revokeObjectURL(url);
    },

    exportPNG() {
      const svgEl = document.querySelector("#lollipop-plot svg");
      if (!svgEl) return;

      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgEl);
      const svgBlob = new Blob([svgString], {
        type: "image/svg+xml;charset=utf-8"
      });

      const url = URL.createObjectURL(svgBlob);
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = svgEl.clientWidth * 2;
        canvas.height = svgEl.clientHeight * 2;
        const ctx = canvas.getContext("2d");
        ctx.scale(2, 2);
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);

        const pngUrl = canvas.toDataURL("image/png");
        const a = document.createElement("a");
        a.href = pngUrl;
        a.download = `${this.protein_id}_lollipop.png`;
        a.click();
      };
      img.src = url;
    }
  },

  watch: {
    selectedFilter() {
      this.schedulePlot();
    },
    tdata: {
      deep: true,
      immediate: true,
      handler() {
        this.schedulePlot();
      }
    },
    protein_id(newVal, oldVal) {
      if (newVal !== oldVal) this.schedulePlot();
    }
  },

  mounted() {
    this.$nextTick(() => this.schedulePlot());

    const container = document.getElementById("lollipop-plot");
    if (!container) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.schedulePlot();
    });
    this.resizeObserver.observe(container);
  },

  beforeDestroy() {
    this.resizeObserver?.disconnect();
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }
};
</script>


<style scoped>
.lollipop-container {
  width: 100%;
  height: 320px;
  position: relative;
}

.kb-tooltip {
  background: #eee;
  border: 1px white solid;
  border-radius: 5px;
  color: black;
  padding: 5px;
  position: fixed;
  z-index: 10;
  display: none;
}
.lollipop-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 5;
}

.lollipop-container ::v-deep .lollipop-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.72);
  font-size: 1.25rem;
  font-weight: 600;
}

</style>

